import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";

/**
 * @typedef {object} PDFEvents
 * @property {string} search - The current search query.
 * @property {number} page - The currently viewed page number (1-based).
 * @property {string} zoom - The current zoom level (e.g., "auto", "100%").
 * @property {number} rotate - The current rotation in degrees (0, 90, 180, 270).
 * @property {number} totalPages - The total number of pages in the PDF document.
 * @property {boolean} thumbDrawerOpen - A boolean indicating if the thumbnail drawer is open.
 */

/**
 * @typedef {object} InitialState
 * @property {import("pdfjs-dist").PDFDocumentProxy} document - The loaded PDF document proxy from pdf.js.
 * @property {import("pdfjs-dist/web/pdf_viewer.mjs").EventBus} eventBus - The event bus instance from pdf.js for communication.
 * @property {import("pdfjs-dist/web/pdf_viewer.mjs").PDFViewer} pdfViewer - The PDF viewer instance from pdf.js for rendering.
 * @property {import("pdfjs-dist/web/pdf_viewer.mjs").PDFFindController} pdfFindController - The PDF find controller instance from pdf.js for searching.
 * @property {import("pdfjs-dist/web/pdf_viewer.mjs").PDFLinkService} pdfLinkService - The PDF link service instance from pdf.js for handling links.
 */

/** @type {InitialState} */
const initialState = {
	document: null,
	eventBus: null,
	pdfViewer: null,
	pdfLinkService: null,
	pdfFindController: null,
};

/** @type {PDFEvents} events - An object containing various viewer state properties. */
const viewerStoreInitialState = {
	search: "",
	page: 1,
	zoom: "auto",
	rotate: 0,
	totalPages: 0,
	thumbDrawerOpen: false,
};

export const pdfStore = createStore(() => initialState);

export const usePdfViewerStore = create(
	persist(() => viewerStoreInitialState, {
		name: "PDF_VIEWER_STORE",
		partialize: (state) => ({
			events: {
				page: state.page,
				search: state.search,
				zoom: state.zoom,
			},
		}),
		storage: createJSONStorage(() => sessionStorage),
	})
);

export const setDocument = (document) =>
	pdfStore.setState((state) => ({ ...state, document }));
export const setEventBus = (eventBus) =>
	pdfStore.setState((state) => ({ ...state, eventBus }));
export const setPdfViewer = (pdfViewer) =>
	pdfStore.setState((state) => ({ ...state, pdfViewer }));
export const setPdfFindController = (pdfFindController) =>
	pdfStore.setState((state) => ({ ...state, pdfFindController }));
export const setPdfLinkService = (pdfLinkService) =>
	pdfStore.setState((state) => ({ ...state, pdfLinkService }));
export const setSearch = (search) =>
	usePdfViewerStore.setState((state) => ({ ...state, search }));
export const setPage = (page) =>
	usePdfViewerStore.setState((state) => ({ ...state, page }));
export const setZoom = (zoom) =>
	usePdfViewerStore.setState((state) => ({ ...state, zoom }));
export const rotateCw = () =>
	usePdfViewerStore.setState((state) => ({
		...state,
		rotate: state.rotate === 270 ? 0 : state.rotate + 90,
	}));
export const setTotalPages = (totalPages) =>
	usePdfViewerStore.setState((state) => ({
		...state,
		totalPages,
	}));

export const handleThumbnailDrawer = () =>
	usePdfViewerStore.setState((state) => ({
		...state,
		thumbDrawerOpen: !state.thumbDrawerOpen,
	}));

export const scrollToSourceInPdf = (pageNumber) => {
	if (isNaN(pageNumber) === false) {
		let intNum = parseInt(pageNumber);
		if (intNum === 0) {
			intNum = 1;
		}
		const { pdfViewer } = pdfStore.getState();
		// Viewer not loaded yet, just set the page
		// Viewer scrolls to page automatically when it is loaded
		if (pdfViewer == null) {
			setPage(intNum);
			return;
		}
		if (pdfViewer != null) {
			pdfViewer.currentPageNumber = intNum;
		}
	}
};

export const highlightKeywordsInPdf = (keywords = null) => {
	if (keywords?.trim()?.length === 0 || keywords == null) {
		return;
	}
	setSearch(keywords);
	// Viewer not loaded yet,
	// Viewer searches the keywords automatically when it is loaded
	const { eventBus } = pdfStore.getState();
	if (eventBus == null) {
		return;
	}

	try {
		eventBus.dispatch("find", {
			query: keywords,
			highlightAll: true,
			phraseSearch: false,
			caseSensitive: false,
			entireWord: false,
			matchDiacritics: false, // Whether to match diacritics/accents
			ignoreAccents: false, // Whether to ignore accents in search
			fuzzySearch: true, // Whether to perform a fuzzy search (for approximate matches)
		});
	} catch (error) {
		console.log(error);
	}
};

export const clearStore = () => {
	pdfStore.setState(() => initialState);
	usePdfViewerStore.setState(() => viewerStoreInitialState);
};
